import { useCallback, useRef, useEffect } from 'react';

export interface MessageItem {
  id: string | number;
  direction?: 'incoming' | 'outgoing';
  [key: string]: any;
}

/**
 * Hook for message list scroll management.
 * Exposes scroll-to-message and scroll-to-bottom methods.
 * Automatically scrolls to new messages when they're added.
 */
export function useMessageScroll(
  messages: MessageItem[],
  autoScrollToBottomOnMount: boolean = true
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastMessageCountRef = useRef<number>(0);

  // Scroll to a specific message by ID
  const scrollToMessage = useCallback(
    (messageId: string | number) => {
      const element = containerRef.current?.querySelector(
        `[data-message-id="${messageId}"]`
      ) as HTMLElement | null;

      if (element && containerRef.current) {
        const container = containerRef.current;
        const elementTop = element.offsetTop - container.offsetTop;
        const scrollTarget = Math.max(0, elementTop - 100);

        container.scrollTop = scrollTarget;
        return true;
      }

      return false;
    },
    []
  );

  // Scroll to bottom
  const scrollToBottom = useCallback(
    (options?: ScrollIntoViewOptions) => {
      if (containerRef.current) {
        const behavior = options?.behavior || 'auto';
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: behavior as ScrollBehavior,
        });
        return true;
      }
      return false;
    },
    []
  );

  // Scroll to bottom on mount
  useEffect(() => {
    if (autoScrollToBottomOnMount && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [autoScrollToBottomOnMount]);

  // Auto-scroll when new messages arrive
  useEffect(() => {
    const currentCount = messages.length;
    const previousCount = lastMessageCountRef.current;

    // When message count increases, a new message was added
    if (currentCount > previousCount && currentCount > 0) {
      const lastMessage = messages[currentCount - 1];
      
      // Scroll to user messages (outgoing) immediately
      // Scroll to assistant messages (incoming) for visibility
      if (lastMessage) {
        // Update count before returning so subsequent renders don't keep re-triggering
        lastMessageCountRef.current = currentCount;

        // Use small delay to ensure DOM has updated
        const timeoutId = setTimeout(() => {
          scrollToMessage(lastMessage.id);
        }, 0);
        
        return () => clearTimeout(timeoutId);
      }
    }

    lastMessageCountRef.current = currentCount;
  }, [messages, scrollToMessage]);

  return {
    containerRef,
    scrollToMessage,
    scrollToBottom,
  };
}
